import type { SelectTagOption, SelectTagRow } from './types.js';

export function customValueFromQuery<V extends string>(
  query: string,
  allowCustomValue: boolean,
  normalizeCustomValue: ((query: string) => V | null) | undefined,
): V | null {
  if (!allowCustomValue) return null;
  const trimmed = query.trim();
  if (trimmed.length === 0) return null;
  if (normalizeCustomValue !== undefined) return normalizeCustomValue(trimmed);
  return trimmed as V;
}

export function optionFromValue<V extends string>(value: V): SelectTagOption<V> {
  return {
    value,
    label: value,
    searchText: value,
  };
}

export function uniqueValues<V extends string>(values: readonly V[]): readonly V[] {
  const seen = new Set<V>();
  const next: V[] = [];
  for (const value of values) {
    if (seen.has(value)) continue;
    seen.add(value);
    next.push(value);
  }
  return next;
}

export function firstEnabledRow<V extends string>(
  rows: readonly SelectTagRow<V>[],
  preferred: number,
): number {
  const current = rows[preferred];
  if (current !== undefined && !current.disabled) return preferred;
  return nextEnabledRow(rows, -1, 1);
}

export function nextEnabledRow<V extends string>(
  rows: readonly SelectTagRow<V>[],
  start: number,
  direction: 1 | -1,
): number {
  if (rows.length === 0) return 0;
  let index = start;
  for (let step = 0; step < rows.length; step += 1) {
    index = (index + direction + rows.length) % rows.length;
    const row = rows[index];
    if (row !== undefined && !row.disabled) return index;
  }
  return 0;
}
